import React, { useEffect, useState } from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Typography from '@material-ui/core/Typography';
import Divider from '@material-ui/core/Divider';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';

import Networker from '../Utils/Networker';

import '../../css/emailPreview.css';

const EmailPreview = ({
  quillValue,
  openEmailPreview,
  setOpenEmailPreview,
  to = 'Selected Users',
}) => {
  const subject = quillValue?.title || '(No subject)';
  const body = quillValue?.editorHtml || '';
  const host = 'https://adrich.io';

  const [loading, setLoading] = useState(false);
  const [previewError, setPreviewError] = useState('');
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewSubject, setPreviewSubject] = useState(subject);

  useEffect(() => {
    if (!openEmailPreview) {
      return;
    }

    let cancelled = false;

    const fetchPreview = async () => {
      setLoading(true);
      setPreviewError('');
      setPreviewHtml('');
      setPreviewSubject(subject);

      try {
        const res = await Networker.post({
            root: 'aws',
            inner: 'email-template/preview',
            body: {
                templateName: 'EmailMessage3',
                subject,
                message: body,
                host,
            },
        });

        if (cancelled) {
          return;
        }

        if (!res.body?.ok) {
          throw new Error(res.body?.error || 'Failed to generate email preview');
        }

        setPreviewHtml(res.body.html || body);
        setPreviewSubject(res.body.subject || subject);
      } catch (err) {
        console.error('Email preview error:', err);

        if (!cancelled) {
          setPreviewError(err.message || 'Failed to generate email preview');
          setPreviewHtml(body);
          setPreviewSubject(subject);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchPreview();

    return () => {
      cancelled = true;
    };
  }, [openEmailPreview, subject, body, host]);

  return (
    <Dialog
      open={openEmailPreview}
      onClose={() => setOpenEmailPreview(false)}
      maxWidth="md"
      fullWidth
      aria-labelledby="email-preview-dialog-title"
    >
      <DialogTitle id="email-preview-dialog-title">
        Email Preview
      </DialogTitle>

      <DialogContent style={{ backgroundColor: '#f4f4f4', padding: 24 }}>
        <div
          style={{
            maxWidth: 720,
            margin: '0 auto',
            backgroundColor: '#ffffff',
            border: '1px solid #ddd',
            borderRadius: 8,
            overflow: 'hidden',
            boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
          }}
        >
          <div
            style={{
              padding: '16px 24px',
              backgroundColor: '#fafafa',
              borderBottom: '1px solid #e0e0e0',
            }}
          >
            <Typography variant="caption" color="textSecondary">
              From
            </Typography>
            <Typography variant="body2" style={{ marginBottom: 8 }}>
              Adrich &lt;getintouch@adrich.io&gt;
            </Typography>

            <Typography variant="caption" color="textSecondary">
              To
            </Typography>
            <Typography variant="body2" style={{ marginBottom: 8 }}>
              {to}
            </Typography>

            <Typography variant="caption" color="textSecondary">
              Subject
            </Typography>
            <Typography variant="h6" style={{ fontWeight: 600 }}>
              {previewSubject}
            </Typography>
          </div>

          <div
            style={{
              padding: 24,
              minHeight: 220,
              fontFamily: 'Arial, Helvetica, sans-serif',
              fontSize: 14,
              lineHeight: 1.5,
              color: '#222',
            }}
          >
            {loading && (
              <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
                <CircularProgress />
              </div>
            )}

            {!loading && previewError && (
              <Typography color="error" style={{ marginBottom: 12 }}>
                {previewError}
              </Typography>
            )}

            {!loading && (
              <div
                className="email-preview-body"
                dangerouslySetInnerHTML={{
                  __html: previewHtml || body,
                }}
              />
            )}
          </div>

          <Divider />

          <div
            style={{
              padding: '16px 24px',
              backgroundColor: '#fafafa',
              fontFamily: 'Arial, Helvetica, sans-serif',
              fontSize: 12,
              color: '#777',
            }}
          >
            <Typography variant="caption" color="textSecondary">
              Template: EmailMessage3
            </Typography>
            <br />
            <Typography variant="caption" color="textSecondary">
              Host: {host}
            </Typography>
          </div>

          <Divider />

          <div
            style={{
              padding: '12px 24px',
              backgroundColor: '#fafafa',
              display: 'flex',
              justifyContent: 'flex-end',
            }}
          >
            <Button
              color="primary"
              variant="contained"
              onClick={() => setOpenEmailPreview(false)}
            >
              Close
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EmailPreview;